import {
  App as AntdApp,
  Avatar,
  Button,
  ConfigProvider,
  Drawer,
  Dropdown,
  Grid,
  Layout,
  Menu,
  Space,
  Typography,
} from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import './i18n/config';
import { baseLanguage, textLang } from './i18n/locale';
import './App.css';
import {
  BankOutlined,
  BarChartOutlined,
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  EyeOutlined,
  FileTextOutlined,
  FormOutlined,
  GiftOutlined,
  LineChartOutlined,
  LogoutOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  ScheduleOutlined,
  SettingOutlined,
  SoundOutlined,
  TagsOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import enUS from 'antd/locale/en_US';
import huHU from 'antd/locale/hu_HU';
import LanguageSwitcher from './components/LanguageSwitcher';
import Logomark from './components/Logomark';
import MasqueradeBanner from './components/MasqueradeBanner';
import MasqueradeStartModal from './components/MasqueradeStartModal';
import { useAuthStore } from './store/authStore';

// easyevnts brand palette (mirrors :root vars in index.css for AntD tokens)
const brand = {
  paper: '#f4efe6',
  paperLight: '#fbf7ee',
  ink: '#1a1612',
  inkSoft: '#5a4e42',
  inkFaded: '#8a7c6e',
  vermilion: '#d7351c',
  vermilionDark: '#a4250f',
};

import AISettings from './pages/AISettings';
import Analytics from './pages/Analytics';
import CheckIn from './pages/CheckIn';
import Companies from './pages/Companies';
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import EmailTemplates from './pages/EmailTemplates';
import EventDetails from './pages/EventDetails';
import EventSettings from './pages/EventSettings';
import Events from './pages/Events';
import FAQs from './pages/FAQs';
import FeedbackAnalytics from './pages/FeedbackAnalytics';
import FeedbackForms from './pages/FeedbackForms';
import ForgotPassword from './pages/ForgotPassword';
// Pages
import Login from './pages/Login';
import MyOrganization from './pages/MyOrganization';
import Notifications from './pages/Notifications';
import Organizations from './pages/Organizations';
import Raffle from './pages/Raffle';
import Register from './pages/Register';
import Registrations from './pages/Registrations';
import ResetPassword from './pages/ResetPassword';
import Sessions from './pages/Sessions';
import Speakers from './pages/Speakers';
import Sponsors from './pages/Sponsors';
import Tickets from './pages/Tickets';
import TicketTemplates from './pages/TicketTemplates';
import Users from './pages/Users';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  // Only show the spinner during the initial auth probe (before persist
  // hydration / first /auth/profile resolves). Once we have a session, keep
  // rendering children even if a background isLoading flicks true — otherwise
  // ProtectedRoute unmounts children mid-flight and any useEffect inside them
  // remounts, refetches, and can trigger refetch loops.
  if (isLoading && !isAuthenticated) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, fetchProfile } = useAuthStore();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [masqueradeOpen, setMasqueradeOpen] = useState(false);

  // Close drawer when route changes (user navigated via menu item)
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    // Only refetch when the field is *missing* from the persisted user
    // (legacy state from an older login response). For SUPER_ADMIN /
    // ATTENDEE etc. `organizationId` is intentionally `null`, and `!null`
    // would loop fetchProfile → isLoading flip → remount → fetchProfile.
    if (user && user.organizationId === undefined) {
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build menu items based on user role
  const menuItems = [];

  // Dashboard - everyone can see
  menuItems.push({
    key: '/',
    icon: <DashboardOutlined />,
    label: t('navigation.dashboard'),
  });

  // Events - everyone can see
  menuItems.push({
    key: '/events',
    icon: <CalendarOutlined />,
    label: t('navigation.events'),
  });

  // Organizations - only SUPER_ADMIN sees the list
  if (user?.role === 'SUPER_ADMIN') {
    menuItems.push({
      key: '/organizations',
      icon: <BankOutlined />,
      label: t('navigation.organizations'),
    });
  }

  // Users - everyone can see (but filtered by permission)
  menuItems.push({
    key: '/users',
    icon: <TeamOutlined />,
    label: t('navigation.users'),
  });

  // Companies - SUPER_ADMIN and ORGANIZATION_ADMIN
  if (['SUPER_ADMIN', 'ORGANIZATION_ADMIN'].includes(user?.role || '')) {
    menuItems.push({
      key: '/companies',
      icon: <BankOutlined />,
      label: t('navigation.companies') || 'Companies',
    });
  }

  // Sessions
  menuItems.push({
    key: '/sessions',
    icon: <ScheduleOutlined />,
    label: t('navigation.sessions'),
  });

  // Speakers
  menuItems.push({
    key: '/speakers',
    icon: <SoundOutlined />,
    label: t('navigation.speakers'),
  });

  // Sponsors
  menuItems.push({
    key: '/sponsors',
    icon: <TrophyOutlined />,
    label: t('navigation.sponsors'),
  });

  // FAQs
  menuItems.push({
    key: '/faqs',
    icon: <QuestionCircleOutlined />,
    label: t('navigation.faqs'),
  });

  // Documents
  menuItems.push({
    key: '/documents',
    icon: <FileTextOutlined />,
    label: t('navigation.documents'),
  });

  // Check-in
  menuItems.push({
    key: '/checkin',
    icon: <CheckCircleOutlined />,
    label: t('navigation.checkin'),
  });

  // Tickets
  menuItems.push({
    key: '/tickets',
    icon: <TagsOutlined />,
    label: t('navigation.tickets'),
  });

  // Registrations — admins only
  if (['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'EVENT_ADMIN'].includes(user?.role || '')) {
    menuItems.push({
      key: '/registrations',
      icon: <FormOutlined />,
      label: t('navigation.registrations'),
    });
  }

  // Ticket Templates - SUPER_ADMIN and ORGANIZATION_ADMIN
  if (['SUPER_ADMIN', 'ORGANIZATION_ADMIN'].includes(user?.role || '')) {
    menuItems.push({
      key: '/ticket-templates',
      icon: <TagsOutlined />,
      label: t('navigation.ticket_templates'),
    });
  }

  // AI Settings - org admins and above
  if (['SUPER_ADMIN', 'ORGANIZATION_ADMIN'].includes(user?.role || '')) {
    menuItems.push({
      key: '/ai-settings',
      icon: <RobotOutlined />,
      label: t('navigation.ai_settings'),
    });
  }

  // Feedback - org admins and above
  if (['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'EVENT_ADMIN'].includes(user?.role || '')) {
    menuItems.push({
      key: '/feedback',
      icon: <FormOutlined />,
      label: t('navigation.feedback') || 'Feedback',
    });
  }

  // Notifications - org admins and above
  if (['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'EVENT_ADMIN'].includes(user?.role || '')) {
    menuItems.push({
      key: '/notifications',
      icon: <BellOutlined />,
      label: t('navigation.notifications'),
    });
  }

  // Raffle — same audience as the guard on the backend controller: EVENT_ADMIN
  // and up. The control panel is driven live on stage, so it is deliberately a
  // top-level entry rather than buried under the event.
  if (['SUPER_ADMIN', 'ORGANIZATION_ADMIN', 'EVENT_ADMIN'].includes(user?.role || '')) {
    menuItems.push({
      key: '/raffle',
      icon: <GiftOutlined />,
      label: t('navigation.raffle'),
    });
  }

  // Analytics
  menuItems.push({
    key: '/analytics',
    icon: <BarChartOutlined />,
    label: t('navigation.analytics'),
  });

  // My Organization - at the end for ORGANIZATION_ADMIN
  if (user?.role === 'ORGANIZATION_ADMIN') {
    menuItems.push({
      key: '/my-organization',
      icon: <BankOutlined />,
      label: t('navigation.my_organization'),
    });
  }

  // Maszkolás közben `user.role` már az IMITÁLT szerepkör, tehát ez a menüpont
  // magától eltűnik — a kilépés a felső sávon van, nem itt.
  const canMasquerade = user?.role === 'SUPER_ADMIN' && !user?.masquerade;

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('common.profile'),
    },
    ...(canMasquerade
      ? [
          {
            key: 'masquerade',
            icon: <EyeOutlined />,
            label: t('masquerade.start'),
          },
        ]
      : []),
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('common.settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('common.logout'),
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else if (key === 'masquerade') {
      setMasqueradeOpen(true);
    } else if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'settings') {
      navigate('/settings');
    }
  };

  const sidebarContent = (
    <>
      <div className="sidebar-logo">
        <Logomark size={22} showKicker />
      </div>
      <Menu
        theme="dark"
        mode="inline"
        className="compact-menu"
        selectedKeys={[location.pathname]}
        onClick={({ key }) => navigate(key)}
        items={menuItems}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <MasqueradeBanner />
      <MasqueradeStartModal open={masqueradeOpen} onClose={() => setMasqueradeOpen(false)} />
      {!isMobile && (
        <Sider theme="dark" width={240} className="brand-sider">
          {sidebarContent}
        </Sider>
      )}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={260}
          closable={false}
          styles={{ body: { padding: 0, background: brand.ink } }}
          className="brand-drawer"
        >
          {sidebarContent}
        </Drawer>
      )}
      <Layout>
        <Header className={`brand-header ${isMobile ? 'brand-header--mobile' : ''}`}>
          <Space size={isMobile ? 8 : 12}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: 20 }} />}
                onClick={() => setDrawerOpen(true)}
                aria-label={t('navigation.openMenu')}
              />
            )}
            <Text
              strong
              ellipsis
              style={{
                fontSize: isMobile ? 14 : 16,
                color: brand.ink,
                maxWidth: isMobile ? 160 : 'unset',
              }}
            >
              {user?.organization?.name || 'easyevnts admin'}
            </Text>
          </Space>
          <Space size={isMobile ? 6 : 'middle'}>
            <LanguageSwitcher />
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleMenuClick }}
              placement="bottomRight"
            >
              <Space style={{ cursor: 'pointer' }} size={6}>
                <Avatar
                  size={isMobile ? 28 : 'default'}
                  icon={<UserOutlined />}
                  style={{ background: brand.vermilion, color: brand.paperLight }}
                />
                {!isMobile && (
                  <Text style={{ color: brand.ink }}>
                    {user?.firstName} {user?.lastName}
                  </Text>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content
          className="brand-content"
          style={{
            margin: isMobile ? 12 : 24,
            padding: isMobile ? 12 : 24,
            minHeight: 280,
            borderRadius: isMobile ? 10 : 14,
            border: '1px solid rgba(26, 22, 18, 0.08)',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

const App: React.FC = () => {
  const { isAuthenticated, initialize } = useAuthStore();
  const { i18n } = useTranslation();
  const [antdLocale, setAntdLocale] = useState(enUS);

  // Initialize auth state on app start
  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    // Ant Design saját feliratai (lapozó, „nincs adat", OK / Mégse, dátumválasztó)
    // NEM a mi szótárunkból jönnek, hanem a ConfigProvider `locale`-jából. A
    // locale-csomagok CommonJS modulok, és a bundler interopja szerint vagy maguk
    // a nyelvi objektumok, vagy egy `{ default: … }` burok érkezik ide. Burokként
    // átadva az antd egyetlen kulcsot sem talál meg bennük, és némán a beépített
    // angolra esik vissza — a saját feliratok magyarok maradnak, az antd-éi nem.
    // Pontosan ez a keverék volt látható a magyar panelen: „Új cég létrehozása"
    // fölött „10 / page". A kicsomagolás mindkét alakot elviszi.
    const unwrap = (mod: any) => mod?.default ?? mod;
    // A szerb SZÁNDÉKOSAN a magyar csomagot kapja, nem a szerbet. A `fallbackLng`
    // a szerbet a magyarra irányítja, tehát egy szerbre állított panel feliratai
    // ma magyarul jelennek meg; szerb lapozó és szerb dátumválasztó alattuk
    // kevertebb lenne, mint amit ma lát. Ugyanaz a megfontolás, ami miatt a
    // `textLocaleTag` is a magyar tagre küldi a szerbet. Ha egyszer a szerb
    // szótár teljes lesz, ide az `antd/locale/sr_RS` kerül — és akkor a
    // `textLocaleTag`-et is vissza kell állítani vele együtt.
    const localeMap: { [key: string]: any } = {
      hu: unwrap(huHU),
      en: unwrap(enUS),
      sr: unwrap(huHU),
    };
    // The browser may report a region-qualified language (`en-GB`, `nl-NL`),
    // which never matched the map and silently fell back to Hungarian. Match on
    // the base code and fall back to English, in step with `fallbackLng: 'en'`.
    setAntdLocale(localeMap[baseLanguage(i18n.language)] || unwrap(enUS));
  }, [i18n.language]);

  // A `<html lang>` a bedrótozott `en`-en állt, akármilyen nyelvű volt a panel.
  // A képernyőolvasók ebből döntik el, milyen nyelven mondják fel a szöveget,
  // tehát egy magyar felületet angol kiejtéssel olvastak volna fel.
  //
  // Nem a választott nyelv kerül ide, hanem `textLang` — a szövegé, ami tényleg
  // a képernyőn van. A szerb szótár 1957 kulcsból 39-et fed le, a többi a
  // `fallbackLng` miatt magyarul jelenik meg; `lang="sr"` mellett a felolvasó
  // magyar mondatokat mondana szerb kiejtéssel. Ugyanaz a megfontolás, ami az
  // antd-csomagot és a `textLocaleTag`-et is a magyarra küldi.
  useEffect(() => {
    document.documentElement.lang = textLang(i18n.language);
  }, [i18n.language]);

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        token: {
          colorPrimary: brand.vermilion,
          colorLink: brand.vermilion,
          colorLinkHover: brand.vermilionDark,
          colorBgLayout: brand.paper,
          colorBgContainer: brand.paperLight,
          colorText: brand.ink,
          colorTextSecondary: brand.inkSoft,
          colorTextTertiary: brand.inkFaded,
          colorBorder: 'rgba(26, 22, 18, 0.12)',
          borderRadius: 10,
          fontFamily:
            'Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        },
        components: {
          Layout: {
            headerBg: brand.paperLight,
            siderBg: brand.ink,
            bodyBg: brand.paper,
          },
          Menu: {
            darkItemBg: brand.ink,
            darkSubMenuItemBg: brand.ink,
            darkItemSelectedBg: brand.vermilion,
            darkItemHoverBg: 'rgba(215, 53, 28, 0.16)',
            darkItemColor: 'rgba(251, 247, 238, 0.78)',
            darkItemSelectedColor: brand.paperLight,
          },
          Button: {
            primaryShadow: '0 1px 0 0 rgba(164, 37, 15, 0.45)',
          },
        },
      }}
    >
      {/* Az antd `App` adja a context-tudatos `modal` / `message` példányt
          (`App.useApp()`), ami — a statikus `Modal.confirm`-mal ellentétben —
          látja a fenti témát. Enélkül a megerősítő ablakok antd-kéken és alap
          betűvel jöttek (#145). */}
      <AntdApp>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* #109 — a levélben kapott hivatkozás ide mutat; belépés nélkül elérhető. */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Events />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organizations"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Organizations />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-organization"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <MyOrganization />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Users />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Companies />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tickets"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Tickets />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ticket-templates"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <TicketTemplates />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AISettings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Analytics />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/speakers"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Speakers />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sponsors"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Sponsors />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sessions"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Sessions />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/faqs"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FAQs />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Documents />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkin"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <CheckIn />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:id"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EventDetails />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FeedbackForms />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback/analytics/:id"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <FeedbackAnalytics />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Notifications />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/registrations"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Registrations />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/raffle"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Raffle />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:id/settings"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EventSettings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/:id/email-templates"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <EmailTemplates />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
